<template>
  <q-card flat bordered v-if="stock" class="q-pa-xs">
    <div v-if="stock.sector_name">
      <div
        class="text-subtitle1 text-weight-medium"
        :class="$q.dark.mode ? '' : 'text-primary'"
      >
        {{ stock.name }} ({{ stock.symbol }})

        <q-chip
          v-if="stock.latestTrades"
          class="q-ma-none"
          square
          dense
          :color="stock.latestTrades.last_trade_diff > 0 ? 'green' : 'red'"
          text-color="white"
          :icon="
            stock.latestTrades.last_trade_diff > 0
              ? 'arrow_drop_up'
              : 'arrow_drop_down'
          "
        >
          {{ stock.latestTrades.last_trade }}
          ( {{ stock.latestTrades.last_trade_diff }} |
          {{ stock.latestTrades.last_trade_diff_percentage }}
          % )
        </q-chip>
      </div>
      <div class="text-subtitle1 text-weight-medium text-secondary">
        {{ stock.sector_name }}
      </div>
      <div v-if="[4, 5, 6, 8].includes(stock?.sector)">
        <div v-if="stock.latestReport?.listed_date">
          <div class="row">
            <p>Listed Date</p>
            <q-space />

            {{ stock.latestReport?.listed_date }}
          </div>
          <div class="row">
            <p>Unlocking Date</p>
            <q-space />

            {{ stock.latestReport?.expired_date }}
          </div>
          <div v-if="[5].includes(stock?.sector)">
            <div class="row">
              <p>Megawatt Capacity</p>
              <q-space />

              {{ stock.latestReport?.mega_watt_capacity }}
              <span class="text-bold q-pl-xs"> MW </span>
            </div>
            <div class="row">
              <p>Megawatt per Cost</p>
              <q-space />

              {{
                amountConversion(stock.latestReport?.mega_watt_per_cost) ?? "-"
              }}
            </div>
          </div>
          <q-separator spaced />
        </div>
      </div>

      <div class="row">
        <p>Public Shares</p>
        <q-space />
        <p class="text-teal">
          <span v-if="stock.latestReport">
            {{
              amountConversion(
                (stock.public_shares / 10000) *
                  stock.latestReport?.paidup_capital
              )
            }}
            units
          </span>
          <span> ( {{ stock.public_shares }} % ) </span>
        </p>
      </div>

      <div class="row">
        <p>Promoter Shares</p>
        <q-space />
        <span v-if="[4, 5, 6, 8].includes(stock?.sector)">
          <span
            v-if="
              stock.latestReport?.expired_date &&
              new Date(stock.latestReport.expired_date) > new Date()
            "
          >
            <q-icon class="q-pa-xs" color="red" name="lock">
              <q-tooltip class="bg-primary">Locked</q-tooltip>
            </q-icon>
          </span>
          <span
            v-else-if="
              stock.latestReport?.expired_date &&
              new Date(stock.latestReport.expired_date) <= new Date()
            "
          >
            <q-icon class="q-pa-xs" color="green" name="lock_open">
              <q-tooltip class="bg-primary">Unlocked</q-tooltip>
            </q-icon>
          </span>
        </span>

        <p class="text-teal">
          <span v-if="stock.latestReport">
            {{
              amountConversion(
                (stock.promoter_shares / 10000) *
                  stock.latestReport?.paidup_capital
              )
            }}
            units
          </span>
          <span> ( {{ stock.promoter_shares }} %) </span>
        </p>
      </div>
      <div v-if="[4, 5, 6, 8].includes(stock?.sector)" class="row">
        <p>Local Shares</p>
        <q-space />
        <p v-if="stock.local" class="text-teal">
          <span
            v-if="
              stock.latestReport?.expired_date &&
              new Date(stock.latestReport.expired_date) > new Date()
            "
          >
            <q-icon class="q-pa-xs" color="red" name="lock">
              <q-tooltip class="bg-primary">Locked</q-tooltip>
            </q-icon>
          </span>
          <span
            v-else-if="
              stock.latestReport?.expired_date &&
              new Date(stock.latestReport.expired_date) <= new Date()
            "
          >
            <q-icon class="q-pa-xs" color="green" name="lock_open">
              <q-tooltip class="bg-primary">Unlocked</q-tooltip>
            </q-icon>
          </span>

          <span v-if="stock.latestReport">
            {{
              amountConversion(
                (stock?.local / 10000) * stock.latestReport?.paidup_capital
              )
            }}
            units
          </span>
          <span> ( {{ stock.local }} %) </span>
        </p>
      </div>

      <div class="row">
        <p>Share Registrar</p>
        <q-space />
        <p class="text-teal">
          {{ stock.share_registar }}
        </p>
      </div>
    </div>
    <div class="q-pt-sm">
      <span class=""> As of : </span>
      <span class="text-teal text-body2 q-pl-xs">
        {{ $store.state.market.asOf }}</span
      >
    </div>
    <q-separator spaced />

    <div v-if="stock.latestReport">
      <div class="row">
        <p>Paid-up Capital</p>
        <q-space />

        {{ amountConversion(stock.latestReport.paidup_capital) }}
      </div>
    </div>
    <div v-if="stock.latestTrades">
      <!-- <div class="row">
        <p>Market Price</p>
        <q-space />
        <p
          :style="{
            color: stock.latestTrades.last_trade_diff > 0 ? 'green' : 'red',
          }"
        >
          {{ stock.latestTrades.last_trade }} (
          {{ stock.latestTrades.last_trade_diff }} )
        </p>
      </div> -->
      <div class="row">
        <p>Tot. Market Cap</p>
        <q-space />
        <p>
          {{
            amountConversion(
              (stock.latestTrades.last_trade *
                stock.latestReport?.paidup_capital) /
                100
            )
          }}
        </p>
      </div>
      <div class="row">
        <p>Float Cap</p>
        <q-space />
        <p>
          {{
            amountConversion(
              stock.latestTrades.last_trade *
                ((stock.public_shares / 10000) *
                  stock.latestReport?.paidup_capital)
            )
          }}
        </p>
      </div>
      <div class="row" v-if="getStockCategory(stock) !== 'Unknown'">
        Float Type
        <q-space />
        <p>
          {{ getStockCategory(stock) }}
        </p>
      </div>
      <div class="row">
        <p>Today's Amount</p>
        <q-space />
        {{ amountConversion(stock.latestTrades.amount) }}
      </div>
      <q-separator spaced />

      <div class="row">
        <p>52 Weeks High / Low</p>
        <q-space />
        {{ stock.latestTrades.week_high_52 }} ~
        {{ stock.latestTrades.week_low_52 }}
      </div>
      <div class="row">
        <p>120 Days Average</p>
        <q-space />
        {{ stock.latestTrades.days_avg_120 }}
      </div>
      <div class="row">
        <p>180 Days Average</p>
        <q-space />
        {{ stock.latestTrades.days_avg_180 }}
      </div>

      <q-separator spaced />

      <div class="row">
        <p>All Time High Volume</p>
        <q-space />
        {{ amountConversion(stock.latestTrades?.all_time_high_volume) }}
      </div>
      <div class="row">
        <p>52 Weeks High / Low Volume</p>
        <q-space />
        {{ amountConversion(stock.latestTrades.weeks_high_volume_52) }} ~
        {{ amountConversion(stock.latestTrades.weeks_low_volume_52) }}
      </div>
      <div class="row">
        <p>50 Days Average Volume</p>
        <q-space />
        {{ amountConversion(stock.latestTrades.days_avg_volume_50) }}
      </div>
    </div>

    <q-separator spaced />
    <div v-if="stock.latestReport">
      <p class="text-body1 text-center">
        <q-chip color="teal" text-color="white" icon="bookmark">
          {{ stock.latestReport.quarter.toUpperCase() }} -
          {{ stock.latestReport.fiscal_year }}
        </q-chip>
      </p>
      <div class="row">
        <p>
          EPS
          <span style="font-style: italic">(Reported) </span>
        </p>
        <q-space />

        {{ stock.latestReport.eps }}
      </div>
      <div class="row">
        <p>
          EPS
          <span style="font-style: italic">(Annualized)</span>
        </p>
        <q-space />

        {{ stock.latestReport.eps_a }}
      </div>

      <div class="row">
        <p>Dis Eps</p>
        <q-space />

        {{ stock.latestReport?.dps }}
      </div>

      <div class="row">
        <p>Net Worth</p>
        <q-space />

        {{ stock.latestReport.net_worth }}
      </div>
      <div class="row">
        <p>NPL</p>
        <q-space />

        {{ stock.latestReport.npl }}
      </div>

      <div class="row">
        <p>P/E Ratio</p>
        <q-space />

        {{ stock.latestReport.pe_ratio }}
      </div>

      <div class="row">
        <p>P/B Ratio</p>
        <q-space />

        {{
          (
            stock.latestTrades?.last_trade / stock.latestReport?.net_worth
          )?.toFixed(2)
        }}
      </div>

      <div class="row">
        <p>ROE</p>
        <q-space />

        {{ stock.latestReport.roe }}
      </div>
      <div class="row">
        <p>ROA</p>
        <q-space />

        {{ stock.latestReport.roa }}
      </div>
      <div class="row">
        <p>Growth Rate</p>
        <q-space />

        {{ stock.latestReport.growth_rate }}
      </div>

      <div class="row">
        <p>PEG Value</p>
        <q-space />

        {{ stock.latestReport.peg_value }}
      </div>
      <div class="row">
        <p>Graham Number</p>
        <q-space />

        {{ stock.latestReport.graham_number }}
      </div>
      <div class="row">
        <p>Adjusted Graham Number</p>
        <q-space />

        {{ stock.latestReport.adjusted_graham_number }}
      </div>
    </div>

    <q-separator spaced />

    <div v-if="stock.latestBonus">
      <div class="row">
        <p>Cash Bonus</p>
        <q-space />
        <span>
          {{ stock.latestBonus.cash }}
        </span>
        <span class="text-secondary q-ml-xs"
          >( FY: {{ stock.latestBonus.fiscal_year }})
        </span>
      </div>
      <div class="row">
        <p>Bonus Shares</p>
        <q-space /> {{ stock.latestBonus.bonus }}
        <span class="text-secondary q-ml-xs"
          >( FY: {{ stock.latestBonus.fiscal_year }})
        </span>
      </div>
      <div class="row">
        <p>Total Bonus</p>
        <q-space />

        {{ stock.latestBonus.total }}
        <span class="text-secondary q-ml-xs"
          >( FY: {{ stock.latestBonus.fiscal_year }})
        </span>
      </div>
    </div>
  </q-card>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import useCore from "src/use/core";
import versionTwo from "src/use/datav2";
import { useStore } from "vuex";
import { useQuasar } from "quasar";
import useUtils from "src/use/utils";
import useData from "src/use/data";

const props = defineProps({
  symbol: {
    type: String,
    required: true,
  },
});

function getStockCategory(stock) {
  if (
    floatCapData.value.high_cap_stocks
      .map((i) => i.symbol)
      .includes(stock.symbol)
  ) {
    return "High Cap";
  } else if (
    floatCapData.value.mid_cap_stocks
      .map((i) => i.symbol)
      .includes(stock.symbol)
  ) {
    return "Mid Cap";
  } else if (
    floatCapData.value.low_cap_stocks
      .map((i) => i.symbol)
      .includes(stock.symbol)
  ) {
    return "Low Cap";
  } else if (
    floatCapData.value.ultra_low_cap_stocks
      .map((i) => i.symbol)
      .includes(stock.symbol)
  ) {
    return "Ultra Low Cap";
  }
  return "Unknown";
}

const { stockSearch, allSector, stockReport, bonus, floatCap } = useCore();
const { HighLowAvgSymbolCount } = versionTwo();
const $q = useQuasar();
const $store = useStore();
const floatCapData = ref({});
const stock = ref(null);
const { getAllSectors, amountConversion } = useUtils();

const loadFloatCap = (symbol) => {
  if ($store.state?.core?.floatCapDataSymbol[symbol]) {
    floatCapData.value = $store.state?.core?.floatCapDataSymbol[symbol];
    return;
  }
  if ($store.state?.core?.floatCapData) {
      floatCapData.value = $store.state?.core?.floatCapData;
  } else {
    floatCap(symbol).then((res) => {
      floatCapData.value = res.data;
      $store.commit("core/setFloatCapDataSymbol", { symbol, data: res.data });
    });
  }
};
const getEssentialStats = (symbol) => {
  if (symbol === null) {
    return;
  }
  let allSectorsList = getAllSectors().map((i) => i.symbol);
  if (!allSectorsList.includes(symbol)) {
    stockSearch(symbol)
      .then((data) => {
        stock.value = data;
        allSector().then((res) => {
          stock.value.sector_name = res.data.find(
            (sector) => sector.id == stock.value.sector
          ).name;
        });

        if ($store.state.core.historyDataSearch) {
          var tradingData52Weeks = $store.state.core.historyDataSearch?.slice(
            0,
            312
          );

          if (tradingData52Weeks) {
            stock.value.latestTrades = {
              last_trade:
                $store.state.core.historyDataSearch[0]?.close?.toFixed(2),
              last_trade_diff: (
                $store.state.core.historyDataSearch[0]?.close -
                $store.state.core.historyDataSearch[1]?.close
              )?.toFixed(2),
              last_trade_diff_percentage: (
                (($store.state.core.historyDataSearch[0]?.close -
                  $store.state.core.historyDataSearch[1]?.close) /
                  $store.state.core.historyDataSearch[1]?.close) *
                100
              )?.toFixed(2),
              amount: tradingData52Weeks?.slice(0, 7).filter((i) => {
                return i.date === $store.state.market.asOf;
              })[0].amount,
            };
          }
        } else {
          $store.dispatch("core/getHistoryDataSearch", symbol).then(() => {
            var tradingData52Weeks = $store.state.core.historyDataSearch?.slice(
              0,
              312
            );

            if (tradingData52Weeks) {
              stock.value.latestTrades = {
                last_trade:
                  $store.state.core.historyDataSearch[0]?.close?.toFixed(2),
                last_trade_diff: (
                  $store.state.core.historyDataSearch[0]?.close -
                  $store.state.core.historyDataSearch[1]?.close
                )?.toFixed(2),
                last_trade_diff_percentage: (
                  (($store.state.core.historyDataSearch[0]?.close -
                    $store.state.core.historyDataSearch[1]?.close) /
                    $store.state.core.historyDataSearch[1]?.close) *
                  100
                )?.toFixed(2),
                amount: tradingData52Weeks?.slice(0, 7).filter((i) => {
                  return i.date === $store.state.market.asOf;
                })[0].amount,
              };
            }
          });
        }

        HighLowAvgSymbolCount(symbol).then((res) => {
          stock.value.latestTrades = stock.value.latestTrades || {};

          const data = res.data;

          stock.value.latestTrades.week_high_52 = data?.weeks_high_52;
          stock.value.latestTrades.week_low_52 = data?.weeks_low_52;

          stock.value.latestTrades.days_avg_120 =
            data?.days_avg_120?.toFixed(2);
          stock.value.latestTrades.days_avg_180 =
            data?.days_avg_180?.toFixed(2);

          stock.value.latestTrades.all_time_high_volume =
            data?.all_time_high_volume;
          stock.value.latestTrades.weeks_high_volume_52 =
            data?.weeks_high_volume_52;
          stock.value.latestTrades.weeks_low_volume_52 =
            data?.weeks_low_volume_52;
          stock.value.latestTrades.days_avg_volume_50 =
            data?.days_avg_volume_50;
        });

        stockReport(stock.value.id).then((res) => {
          var latestStockReport = res.data[0];
          stock.value.latestReport = {
            fiscal_year: latestStockReport?.fiscal_year,
            quarter: latestStockReport?.quarter,
            eps: latestStockReport.eps?.toFixed(2),
            eps_a: latestStockReport.eps_a?.toFixed(2),
            npl: latestStockReport.npl?.toFixed(2),
            roe: latestStockReport.roe?.toFixed(2),
            roa: latestStockReport.roa?.toFixed(2),
            net_worth: latestStockReport.net_worth?.toFixed(2),
            growth_rate: latestStockReport.growth_rate?.toFixed(2),
            paidup_capital: latestStockReport?.paidup_capital,
            graham_number: latestStockReport?.gram_value?.toFixed(2),
            peg_value: latestStockReport.peg_value?.toFixed(2),
            pe_ratio: latestStockReport.pe_ratio?.toFixed(2),
            listed_date: latestStockReport?.listed_date,
            expired_date: latestStockReport?.expired_date,
            mega_watt_capacity: latestStockReport?.mega_watt_capacity,
            mega_watt_per_cost: latestStockReport?.mega_watt_per_cost,
            dps: latestStockReport?.dps,
          };

          stock.value.latestReport["adjusted_graham_number"] =
            parseFloat(stock.value.latestReport?.eps) > 0
              ? (
                  (parseFloat(stock.value.latestReport.eps) *
                    (8.5 + parseFloat(stock.value.latestReport.growth_rate)) *
                    4.4) /
                  6
                )?.toFixed(2)
              : " ";
        });

        bonus(stock.value.symbol).then((res) => {
          var latestStockBonus = res.data[0];

          stock.value.latestBonus = {
            fiscal_year: latestStockBonus?.year,
            bonus: latestStockBonus?.bonus?.toFixed(2),
            cash: latestStockBonus?.cash?.toFixed(2),
            total: latestStockBonus?.total?.toFixed(2),
          };
        });
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        stock.value = stock.value;
      });
  }
};

watch(
  () => $store.state.core.historyDataSearch,
  (newVal, oldVal) => {
    getEssentialStats(props.symbol);
  }
);

watch(
  () => props.symbol,
  (newVal, oldVal) => {
    if (newVal) {
      loadFloatCap(newVal);
      getEssentialStats(newVal);
    }
  },
  { immediate: true }
);
</script>
